import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { fetchData, formatNumber, formatDate, useMenuIdPermissions, getCategoryNameById, getTargetMeta, formatTargetValue, getEncryptedRoute, toasterrormsg } from '../../../reusable'
import { PageTitle } from '../../../_metronic/layout/core'
import { KTIcon, toAbsoluteUrl } from '../../../_metronic/helpers'
import { Tr } from '../../../_metronic/i18n/Tr'
import { Ad } from '../types/ads'
import Fancybox from '../Fancybox'
import moment from 'moment'
import { regionList, interestList } from '../types/general'

const STATUS_CONFIG: Record<string, { label: string; class: string }> = {
  DRAFT: { label: 'Draft', class: 'ad-detail-status--draft' },
  UNDER_REVIEW: { label: 'Under Review', class: 'ad-detail-status--under_review' },
  APPROVED: { label: 'Approved', class: 'ad-detail-status--approved' },
  ACTIVATED: { label: 'Active', class: 'ad-detail-status--activated' },
  REJECTED: { label: 'Rejected', class: 'ad-detail-status--rejected' },
  COMPLETED: { label: 'Completed', class: 'ad-detail-status--completed' },
  PAUSED: { label: 'Paused', class: 'ad-detail-status--paused' },
}

const LANGUAGES = [
  { key: 'en', label: 'English', flag: '/media/flags/united-states.svg' },
  { key: 'ru', label: 'Russian', flag: '/media/flags/russia.svg' },
  { key: 'uz', label: 'Uzbek', flag: '/media/flags/uzbekistan.svg' },
]

const getAdScheduleInfo = (startDate: string | null, endDate: string | null, status: string = 'DRAFT') => {
  if (!startDate || !endDate) {
    // Status-only messages when no dates are set
    if (status === 'DRAFT') {
      return {
        icon: 'pencil' as const,
        bgColor: '#F9FAFB',
        borderColor: '#E5E7EB',
        textColor: 'text-muted',
        title: 'Draft',
        message: 'This ad is still in draft mode and has not been submitted for review yet.',
      }
    }
    if (status === 'UNDER_REVIEW') {
      return {
        icon: 'eye' as const,
        bgColor: '#EFF6FF',
        borderColor: '#BFDBFE',
        textColor: 'text-info',
        title: 'Under Review',
        message: 'This ad is currently being reviewed by the admin team. You will be notified once a decision is made.',
      }
    }
    if (status === 'REJECTED') {
      return {
        icon: 'cross-circle' as const,
        bgColor: '#FEF2F2',
        borderColor: '#FECACA',
        textColor: 'text-danger',
        title: 'Rejected',
        message: 'This ad has been rejected. Please review the rejection reason and make the necessary changes before resubmitting.',
      }
    }
    return null
  }

  const today = moment().startOf('day')
  const start = moment(startDate).startOf('day')
  const end = moment(endDate).startOf('day')

  // Status-based messages take priority over date-based logic
  if (status === 'DRAFT') {
    return {
      icon: 'pencil' as const,
      bgColor: '#F9FAFB',
      borderColor: '#E5E7EB',
      textColor: 'text-muted',
      title: 'Draft',
      message: `This ad is still in draft mode. It is scheduled from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')} but has not been submitted yet.`,
    }
  }

  if (status === 'UNDER_REVIEW') {
    return {
      icon: 'eye' as const,
      bgColor: '#EFF6FF',
      borderColor: '#BFDBFE',
      textColor: 'text-info',
      title: 'Under Review',
      message: `This ad is currently being reviewed. It is scheduled to run from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')}.`,
    }
  }

  if (status === 'REJECTED') {
    return {
      icon: 'cross-circle' as const,
      bgColor: '#FEF2F2',
      borderColor: '#FECACA',
      textColor: 'text-danger',
      title: 'Rejected',
      message: 'This ad has been rejected. Please review the rejection reason and make the necessary changes before resubmitting.',
    }
  }

  if (status === 'PAUSED') {
    const daysRemaining = end.diff(today, 'days')
    return {
      icon: 'pause-circle' as const,
      bgColor: '#FFFBEB',
      borderColor: '#FDE68A',
      textColor: 'text-warning',
      title: 'Paused',
      message: today.isSameOrBefore(end)
        ? `This ad is currently paused and not being shown to users. It was scheduled until ${formatDate(endDate, 'DD-MM-YYYY')} (${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining).`
        : `This ad was paused. Its schedule was from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')}.`,
    }
  }

  if (status === 'COMPLETED') {
    return {
      icon: 'calendar-tick' as const,
      bgColor: '#DBEAFE',
      borderColor: '#93C5FD',
      textColor: 'text-primary',
      title: 'Completed',
      message: `This ad ran from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')} and has ended.`,
    }
  }

  // For APPROVED and ACTIVATED statuses, use date-based logic
  if (status === 'APPROVED') {
    if (today.isBefore(start)) {
      const daysUntilStart = start.diff(today, 'days')
      return {
        icon: 'verify' as const,
        bgColor: '#F0FDF4',
        borderColor: '#BBF7D0',
        textColor: 'text-success',
        title: 'Approved',
        message: `This ad has been approved and is scheduled to go live from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')} (starts in ${daysUntilStart} day${daysUntilStart !== 1 ? 's' : ''}).`,
      }
    }
    return {
      icon: 'verify' as const,
      bgColor: '#F0FDF4',
      borderColor: '#BBF7D0',
      textColor: 'text-success',
      title: 'Approved',
      message: `This ad has been approved. It is scheduled from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')}.`,
    }
  }

  // ACTIVATED status — date-based logic
  if (today.isBefore(start)) {
    const daysUntilStart = start.diff(today, 'days')
    return {
      icon: 'time' as const,
      bgColor: '#FFF7ED',
      borderColor: '#FED7AA',
      textColor: 'text-warning',
      title: 'Scheduled',
      message: `This ad is activated and in the queue. It will be shown from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')} (starts in ${daysUntilStart} day${daysUntilStart !== 1 ? 's' : ''}).`,
    }
  }
  if (today.isSameOrAfter(start) && today.isSameOrBefore(end)) {
    const daysRemaining = end.diff(today, 'days')
    return {
      icon: 'verify' as const,
      bgColor: '#F0FDF4',
      borderColor: '#BBF7D0',
      textColor: 'text-success',
      title: 'Currently Active',
      message: `This ad is currently live and being shown to users. It will run until ${formatDate(endDate, 'DD-MM-YYYY')} (${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining).`,
    }
  }
  if (today.isAfter(end)) {
    return {
      icon: 'calendar-tick' as const,
      bgColor: '#F3F4F6',
      borderColor: '#D1D5DB',
      textColor: 'text-muted',
      title: 'Completed',
      message: `This ad ran from ${formatDate(startDate, 'DD-MM-YYYY')} to ${formatDate(endDate, 'DD-MM-YYYY')} and has ended.`,
    }
  }
  return null
}

const LangTabs: FC<{ activeLang: string; setActiveLang: (l: string) => void }> = ({ activeLang, setActiveLang }) => (
  <div className='d-flex flex-wrap gap-2 mb-4'>
    {LANGUAGES.map((lang) => (
      <button
        key={lang.key}
        type='button'
        className={`ad-lang-tab ${activeLang === lang.key ? 'ad-lang-tab--active' : ''}`}
        onClick={() => setActiveLang(lang.key)}
      >
        <img src={toAbsoluteUrl(lang.flag)} alt={lang.label} className='ad-lang-flag' />
        <span className='d-none d-sm-inline'>{lang.label}</span>
        <span className='d-inline d-sm-none'>{lang.key.toUpperCase()}</span>
      </button>
    ))}
  </div>
)

const DetailRow: FC<{ label: string; children: React.ReactNode; className?: string }> = ({ label, children, className = '' }) => (
  <div className={`row py-2 py-sm-3 mx-0 ad-detail-row ${className}`}>
    <div className='col-12 col-sm-5 col-md-4 ad-detail-label mb-1 mb-sm-0'>{label}</div>
    <div className='col-12 col-sm-7 col-md-8 ad-detail-value'>{children}</div>
  </div>
)

const ViewBannerAdvertisementDetail: FC = () => {
  const t = Tr()
  const navigate = useNavigate()
  const { urlData } = useMenuIdPermissions() as any
  const adsId = urlData?.adsId || ''

  const [adDetail, setAdDetail] = useState<Ad | null>(null)
    const [regionList, setRegionList] = useState<regionList[]>([]);
  const [interestList, setInterestList] = useState<interestList[]>([]);

  const [isLoading, setIsLoading] = useState(true)
  const [activeLang, setActiveLang] = useState('en')
  const [errorMessage, setErrorMessage] = useState('')
  const [categoryName, setCategoryName] = useState('')
  const [url, setUrl] = useState('')

  const regionListApiCall = async () => {
    try {
      const response = await fetchData('vendor/public/regionList', {})
      if (response.status == '200') {
        const data = response.data
        setRegionList(Array.isArray(data) ? data : []);
      } else {
        console.error(response?.message || 'An error occurred')
        setRegionList([])
      }
    } catch (error) {
      console.error('Error fetching region list:', error)
      setRegionList([])

    }
  }

  const interestListApiCall = async () => {
    try {
      const response = await fetchData('admin/interestlist', {})
      if (response.status == '200') {
        const data = response.data
        setInterestList(Array.isArray(data) ? data : []);
      } else {
        console.error(response?.message || 'An error occurred')
        setInterestList([])
      }
    } catch (error) {
      console.error('Error fetching interest list:', error)
      setInterestList([])
    }
  }


  const adsDetailsApiCall = async () => {
    try {
      setIsLoading(true)
      setErrorMessage('')
      const res = await fetchData('admin/ads/detail', { adsId })
      if (res.status == '200') {
        const data = res?.data
        setAdDetail(data ?? null)
        if (data?.categoryId) {
          const name = await getCategoryNameById(String(data.categoryId))
          setCategoryName(name)
          setUrl(res?.url ?? '')
        }
      } else {
        setErrorMessage(res.message || 'Failed to load advertisement details')
      }
    } catch (error) {
      console.error('Error fetching ad detail:', error)
      setErrorMessage('Something went wrong while fetching advertisement details')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    document.title = 'Insayr | Banner Advertisement Detail'
    regionListApiCall()
    interestListApiCall()
    if (adsId) {
      adsDetailsApiCall()
    } else {
      setIsLoading(false)
      setErrorMessage('No advertisement ID provided')
    }
  }, [adsId])

  if (isLoading) {

    return (
      <div className='d-flex flex-column justify-content-center align-items-center gap-3' style={{ minHeight: 400 }}>
        <span className='spinner-border text-primary' style={{ width: 48, height: 48 }} />
        <p className='ad-section__subtitle mb-0 fs-6'>{t('Loading...')}</p>
      </div>
    )
  }

  if (errorMessage) {

    return (
      <>
        <PageTitle breadcrumbs={[]}>{t('Advertisement Detail')}</PageTitle>
        <div className='d-flex flex-column align-items-center justify-content-center px-3' style={{ minHeight: 400 }}>
          <div className='d-flex align-items-center justify-content-center mb-4' style={{ width: 64, height: 64, borderRadius: 16, background: '#FEE2E2' }}>
            <KTIcon iconName='cross-circle' className='fs-2x text-danger' />
          </div>
          <h4 className='fw-bold text-gray-800 mb-2 text-center'>{t('Error Loading Details')}</h4>
          <p className='text-muted fs-6 text-center mb-5' style={{ maxWidth: 400 }}>{errorMessage}</p>
          <button className='btn btn-light-primary btn-sm d-flex align-items-center gap-2' onClick={() => navigate(-1)}>
            <KTIcon iconName='arrow-left' className='fs-3' /> {t('Go Back')}
          </button>
        </div>
      </>
    )
  }

  if (!adDetail) {
    return (
      <>
        <PageTitle breadcrumbs={[]}>{t('Advertisement Detail')}</PageTitle>
        <div className='d-flex flex-column align-items-center justify-content-center px-3' style={{ minHeight: 400 }}>
          <div className='d-flex align-items-center justify-content-center mb-4' style={{ width: 64, height: 64, borderRadius: 16, background: '#F3F4F6' }}>
            <KTIcon iconName='document' className='fs-2x text-muted' />
          </div>
          <h4 className='fw-bold text-gray-800 mb-2 text-center'>{t('No Details Found')}</h4>
          <p className='text-muted fs-6 text-center mb-5' style={{ maxWidth: 400 }}>
            {t('The advertisement details could not be found. It may have been removed or is no longer available.')}
          </p>
          <button className='btn btn-light-primary btn-sm d-flex align-items-center gap-2' onClick={() => navigate(-1)}>
            <KTIcon iconName='arrow-left' className='fs-3' /> {t('Go Back')}
          </button>
        </div>
      </>
    )
  }

  const status = adDetail?.status
  const statusCfg = status && STATUS_CONFIG[status] ? STATUS_CONFIG[status] : { label: 'Not Available', class: 'ad-detail-status--not-available' }
  const scheduleInfo = adDetail?.startDate && getAdScheduleInfo(adDetail?.startDate, adDetail?.endDate, status)

  const getLangTitle = () => {
    if (activeLang === 'en') return adDetail?.en_name || '-'
    if (activeLang === 'ru') return adDetail?.ru_name || '-'
    return adDetail?.uz_name || '-'
  }
  const getLangDescription = () => {
    if (activeLang === 'en') return adDetail?.en_description || '-'
    if (activeLang === 'ru') return adDetail?.ru_description || '-'
    return adDetail?.uz_description || '-'
  }
  const getLangBannerTitle = () => {
    if (activeLang === 'en') return adDetail?.en_title || '-'
    if (activeLang === 'ru') return adDetail?.ru_title || '-'
    return adDetail?.uz_title || '-'
  }
  const getLangBannerSubtitle = () => {
    if (activeLang === 'en') return adDetail?.en_subtitle || '-'
    if (activeLang === 'ru') return adDetail?.ru_subtitle || '-'
    return adDetail?.uz_subtitle || '-'
  }

  const targets = adDetail?.target || []
  const promotedItems = Array.isArray(adDetail?.actionDetail) ? adDetail.actionDetail : []
  const galleryImages = adDetail?.banners || []
  const getImageUrl = (img: any) => (typeof img === 'string' ? url + img : url + (img?.image ?? '')) || ''

  return (
    <>
      <PageTitle breadcrumbs={[]}>{t('Advertisement Detail')}</PageTitle>

      {/* Header */}
      <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 mb-md-6'>
        <button className='btn btn-light-danger btn-sm d-flex align-items-center gap-2' onClick={() => navigate(-1)}>
          <KTIcon iconName='arrow-left' className='fs-3' /> {t('Back')}
        </button>
        <span className={`ad-detail-status ${statusCfg.class}`}>{t(statusCfg.label)}</span>
      </div>

      {/* Schedule Status Banner */}
      {scheduleInfo && (
        <div
          className='d-flex flex-column flex-sm-row align-items-start gap-3 p-3 p-sm-4 rounded-3 mb-4 mb-md-6'
          style={{ background: scheduleInfo.bgColor, border: `1px solid ${scheduleInfo.borderColor}` }}
        >
          <KTIcon iconName={scheduleInfo.icon} className={`fs-1 ${scheduleInfo.textColor} flex-shrink-0`} />
          <div className='min-w-0'>
            <div className={`fw-bold fs-6 ${scheduleInfo.textColor} mb-1`}>{t(scheduleInfo.title)}</div>
            <p className='text-gray-700 fs-7 mb-0'>{scheduleInfo.message}</p>
          </div>
        </div>
      )}

      {/* Reject Reason */}
      {status === 'REJECTED' && adDetail?.rejectReason && (
        <div className='ad-reject-reason mb-4 mb-md-6'>
          <KTIcon iconName='information-5' className='fs-2 text-danger mt-1 flex-shrink-0' />
          <p className='ad-reject-reason__text mb-0'>
            <strong>{t('Rejection Reason:')}</strong> {adDetail?.rejectReason}
          </p>
        </div>
      )}

      <div className='row g-4 g-xl-6'>
        {/* LEFT COLUMN */}
        <div className='col-12 col-lg-8'>

          {/* Vendor Info */}
          <div className='ad-section'>
            <div className='ad-section__header'>
              <div className='ad-section__icon ad-section__icon--teal flex-shrink-0'>
                <KTIcon iconName='profile-user' className='fs-2 text-info' />
              </div>
              <div className='min-w-0'>
                <h4 className='ad-section__title'>{t('Vendor Information')}</h4>
                <p className='ad-section__subtitle mb-0'>{t('Details about the advertiser')}</p>
              </div>
            </div>
            <div className='d-flex flex-column flex-sm-row align-items-center align-items-sm-start gap-3 p-3 p-sm-4 rounded-3 bg-light border'>
              {adDetail?.logo && (
                <Fancybox delegate='[data-fancybox="vendor-logo"]' options={{ infinite: false }}>
                  <a data-fancybox='vendor-logo' href={url + "vendor/" + adDetail?.logo} className='d-block flex-shrink-0'>
                    <img src={url + "vendor/" + adDetail?.logo} alt='Vendor' className='ad-vendor-avatar' onError={(e) => {
                      e.currentTarget.onerror = null
                      e.currentTarget.src = toAbsoluteUrl('/media/logos/mainLogo.png')
                    }} />
                  </a>
                </Fancybox>
              )}
              <div className='text-center text-sm-start min-w-0 w-100'>
                <div className='fw-bold fs-5 text-dark text-truncate'>{adDetail?.fullName || '-'}</div>
                {adDetail?.email && <div className='text-muted fs-7 text-truncate'>{adDetail?.email}</div>}
                {adDetail?.mobileNumber && <div className='text-muted fs-7'>{adDetail?.mobileNumber}</div>}
                {adDetail?.categoryId && (
                  <span className='badge badge-light-primary mt-2 text-truncate d-inline-block' style={{ maxWidth: '100%' }}>
                    {categoryName || adDetail?.categoryId}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Placement */}
          <div className='ad-section'>
            <div className='ad-section__header'>
              <div className='ad-section__icon ad-section__icon--blue flex-shrink-0'>
                <KTIcon iconName='geolocation-home' className='fs-2 text-primary' />
              </div>
              <div className='min-w-0'>
                <h4 className='ad-section__title'>{t('Ad Placement')}</h4>
                <p className='ad-section__subtitle mb-0'>{t('Where this ad will be displayed')}</p>
              </div>
            </div>
            <div className='d-flex flex-column flex-sm-row align-items-center align-items-sm-start gap-3 p-3 p-sm-4 rounded-3 bg-light border'>
              <div className='ad-placement-card__icon flex-shrink-0'>
                <KTIcon iconName='abstract-26' className='fs-1 text-primary' />
              </div>
              <div className='min-w-0 text-center text-sm-start flex-grow-1'>
                <div className='fw-bold fs-5 text-dark text-break'>{adDetail?.placementName || '-'}</div>
                <p className='text-muted fs-7 mb-0'>{adDetail?.description || '-'}</p>
              </div>
              {adDetail?.amountPerDay && (
                <div className='flex-shrink-0 text-center text-sm-end'>
                  <span className='fw-bolder fs-3 text-dark d-block'>{formatNumber(parseFloat(adDetail?.amountPerDay))}</span>
                  <span className='text-muted fs-8 fw-semibold'>{t('per day')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Targeting */}
          <div className='ad-section'>
            <div className='ad-section__header'>
              <div className='ad-section__icon ad-section__icon--purple flex-shrink-0'>
                <KTIcon iconName='focus' className='fs-2 text-purple' />
              </div>
              <div className='min-w-0'>
                <h4 className='ad-section__title'>{t('Targeting Settings')}</h4>
                <p className='ad-section__subtitle mb-0'>{t('Audience filters applied to this ad')}</p>
              </div>
            </div>
            {targets.length > 0 && targets[0]?.adstargetId !== null ? (
              <div className='row g-3'>
                {targets.map((target: any, idx: number) => {
                  const meta = getTargetMeta(target?.key)
                  const targetIcon = target?.iconName || meta.icon
                  const colorClass = meta.colorClass
                  const displayValue = formatTargetValue(target?.key, target?.value, regionList, interestList)

                  return (
                    <div className='col-6 col-sm-4 col-md-3' key={idx}>
                      <div className='ad-target-card text-center p-3 rounded-3 h-100'>
                        <div className={`d-flex align-items-center justify-content-center mx-auto mb-2 rounded-circle bg-light-${colorClass}`} style={{ width: 40, height: 40 }}>
                          <KTIcon iconName={targetIcon} className={`fs-3 text-${colorClass}`} />
                        </div>
                        <div className='text-muted fw-bold fs-9 text-uppercase mb-1'>{target.key || '-'}</div>
                        <div className='fw-bold fs-7 text-dark text-break'>{displayValue}</div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className='d-flex align-items-start gap-2 text-muted'>
                <KTIcon iconName='information-5' className='fs-3 flex-shrink-0 mt-1' />
                <span className='fs-6'>{t('No specific targeting — ad will be shown to all users')}</span>
              </div>
            )}
          </div>

          {/* Promoted Property */}
          {promotedItems.length > 0 && (
            <div className='ad-section'>
              <div className='ad-section__header'>
                <div className='ad-section__icon ad-section__icon--green flex-shrink-0'>
                  <KTIcon iconName='home-2' className='fs-2 text-success' />
                </div>
                <div className='min-w-0'>
                  <h4 className='ad-section__title'>{t('Promoted Property')}</h4>
                  <p className='ad-section__subtitle mb-0'>{t('The property and items promoted with this ad')}</p>
                </div>
              </div>
              {adDetail?.actionType && (
                <div className='mb-3'>
                  <span className='badge badge-light-primary text-uppercase'>{adDetail?.actionType}</span>
                </div>
              )}
              <div className='row g-3'>
                {promotedItems.map((item: any, idx: number) => (
                  <div className='col-6 col-sm-4 col-md-3' key={idx}>
                    <div className='ad-target-card text-center p-3 rounded-3 h-100'>
                      <div className='d-flex align-items-center justify-content-center mx-auto mb-2 rounded-circle bg-light-primary' style={{ width: 40, height: 40 }}>
                        <KTIcon iconName={item?.en_parentName ? 'home-2' : 'shop'} className='fs-3 text-primary' />
                      </div>
                      {item?.en_parentName && (
                        <div className='text-muted fw-bold fs-9 text-uppercase mb-1'>{item.en_parentName}</div>
                      )}
                      <div className='fw-bold fs-7 text-dark text-break'>{item?.en_name || item?.actionId || '-'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Banner Content */}
          <div className='ad-section'>
            <div className='ad-section__header'>
              <div className='ad-section__icon ad-section__icon--orange flex-shrink-0'>
                <KTIcon iconName='picture' className='fs-2 text-warning' />
              </div>
              <div className='min-w-0'>
                <h4 className='ad-section__title'>{t('Banner Content')}</h4>
                <p className='ad-section__subtitle mb-0'>{t('Banner image and text overlay')}</p>
              </div>
            </div>
            {adDetail?.bannerImage && (
              <div className='mb-4'>
                <Fancybox delegate='[data-fancybox="banner"]' options={{ infinite: false }}>
                  <a data-fancybox='banner' href={url + adDetail?.bannerImage} className='d-block'>
                    <div className='ad-banner-preview'>
                      <img src={url + adDetail?.bannerImage} alt='Banner' style={{ cursor: 'pointer' }} onError={(e) => {
                        e.currentTarget.onerror = null
                        e.currentTarget.src = toAbsoluteUrl('/media/logos/mainLogo.png')
                      }} />
                    </div>
                  </a>
                </Fancybox>
              </div>
            )}
            <LangTabs activeLang={activeLang} setActiveLang={setActiveLang} />
            <DetailRow label={t('Banner Title')}>
              <span className='fw-bold text-break'>{getLangBannerTitle()}</span>
            </DetailRow>
            <DetailRow label={t('Banner Subtitle')}>
              <span className='text-break'>{getLangBannerSubtitle()}</span>
            </DetailRow>
          </div>

          {/* Ad Details */}
          <div className='ad-section'>
            <div className='ad-section__header'>
              <div className='ad-section__icon ad-section__icon--green flex-shrink-0'>
                <KTIcon iconName='document' className='fs-2 text-success' />
              </div>
              <div className='min-w-0'>
                <h4 className='ad-section__title'>{t('Advertisement Details')}</h4>
                <p className='ad-section__subtitle mb-0'>{t('Title, description, and content')}</p>
              </div>
            </div>
            <LangTabs activeLang={activeLang} setActiveLang={setActiveLang} />

            <DetailRow label={t('Title')}>
              <span className='fw-bold fs-6 text-break'>{getLangTitle()}</span>
            </DetailRow>
            <DetailRow label={t('Description')}>
              <div className='ad-description-content text-break' dangerouslySetInnerHTML={{ __html: getLangDescription() }} />
            </DetailRow>
            {adDetail?.promocode && (
              <DetailRow label={t('Promo Code')}>
                <span className='ad-promo-badge text-break'>{adDetail?.promocode}</span>
              </DetailRow>
            )}
            {(adDetail?.hotelName || adDetail?.roomName || adDetail?.promotionItem) && (
              <DetailRow label={t('Promotion Item')}>
                <>
                  {adDetail?.hotelName && (
                    <div className='d-flex align-items-center gap-2 mb-1'>
                      <KTIcon iconName='home-2' className='fs-4 text-muted flex-shrink-0' />
                      <span className='fw-semibold text-break'>{adDetail?.hotelName}</span>
                    </div>
                  )}
                  {adDetail?.roomName && (
                    <div className='d-flex align-items-center gap-2'>
                      <KTIcon iconName='home-1' className='fs-4 text-muted flex-shrink-0' />
                      <span className='text-break'>{adDetail?.roomName}</span>
                    </div>
                  )}
                  {adDetail?.promotionItem && !adDetail?.hotelName && <span className='text-break'>{adDetail?.promotionItem}</span>}
                </>
              </DetailRow>
            )}

            {/* Gallery */}
            {galleryImages.length > 0 && (
              <div className='mt-4'>
                <div className='ad-detail-label mb-3'>{t('Gallery Images')}</div>
                <Fancybox options={{ infinite: true, Thumbs: { type: 'classic' } }}>
                  <div className='row g-2 g-sm-3'>
                    {galleryImages.map((img: any, idx: number) => (
                      <div className='col-6 col-sm-4 col-md-3' key={idx}>
                        <a data-fancybox='gallery' href={getImageUrl(img)} className='ad-gallery__item d-block'>
                          <img src={getImageUrl(img)} alt={`Gallery ${idx + 1}`} className='w-100' onError={(e) => {
                            e.currentTarget.onerror = null
                            e.currentTarget.src = toAbsoluteUrl('/media/logos/mainLogo.png')
                          }} />
                        </a>
                      </div>
                    ))}
                  </div>
                </Fancybox>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className='col-12 col-lg-4 '>

          {/* Banner Summary */}
          <div className='ad-section'>
            <div className='ad-section__header'>
              <div className='ad-section__icon ad-section__icon--blue flex-shrink-0'>
                <KTIcon iconName='clipboard' className='fs-2 text-primary' />
              </div>
              <div className='min-w-0'>
                <h4 className='ad-section__title'>{t('Banner Summary')}</h4>
              </div>
            </div>
            <DetailRow label={t('Banner Title')}>
              <span className='fw-bold text-break'>{adDetail?.en_title || '-'}</span>
            </DetailRow>
            <DetailRow label={t('Status')}>
              <span className={`ad-detail-status ${statusCfg.class}`}>{t(statusCfg.label)}</span>
            </DetailRow>
            <DetailRow label={t('Duration')}>
              <span className='text-nowrap'>{adDetail?.duration ? `${adDetail?.duration} ${adDetail?.durationType}` : '-'}</span>
            </DetailRow>
            <DetailRow label={t('Created')}>
              <span className='text-nowrap'>{adDetail?.created ? formatDate(adDetail?.created, 'DD-MM-YYYY') : '-'}</span>
            </DetailRow>
            {adDetail?.startDate && (
              <DetailRow label={t('Start Date')}>
                <span className='text-nowrap'>{formatDate(adDetail?.startDate, 'DD-MM-YYYY')}</span>
              </DetailRow>
            )}
            {adDetail?.endDate && (
              <DetailRow label={t('End Date')}>
                <span className='text-nowrap'>{formatDate(adDetail?.endDate, 'DD-MM-YYYY')}</span>
              </DetailRow>
            )}
          </div>

          {/* Budget & Payment */}
          <div className='ad-section ad-right-col'>
            <div className='ad-section__header'>
              <div className='ad-section__icon ad-section__icon--green flex-shrink-0'>
                <KTIcon iconName='dollar' className='fs-2 text-success' />
              </div>
              <div className='min-w-0'>
                <h4 className='ad-section__title'>{t('Budget & Payment')}</h4>
                <p className='ad-section__subtitle mb-0'>{t('Billing and payment details')}</p>
              </div>
            </div>
            <div className='ad-payment-summary'>
              <div className='ad-payment-row'>
                <span className='ad-payment-row__label flex-shrink-0'>{t('Placement')}</span>
                <span className='ad-payment-row__value text-end text-break ms-2'>{adDetail?.placementName || '-'}</span>
              </div>
              <div className='ad-payment-row'>
                <span className='ad-payment-row__label flex-shrink-0'>{t('Pricing Model')}</span>
                <span className='ad-payment-row__value text-end'>{t('Per Day')}</span>
              </div>
              <div className='ad-payment-row'>
                <span className='ad-payment-row__label flex-shrink-0'>{t('Duration')}</span>
                <span className='ad-payment-row__value text-end text-nowrap'>{adDetail?.duration ? `${adDetail?.duration} ${adDetail?.durationType}` : '-'}</span>
              </div>
              <div className='ad-payment-row'>
                <span className='ad-payment-row__label flex-shrink-0'>{t('Rate Per Day')}</span>
                <span className='ad-payment-row__value text-end text-nowrap'>{adDetail?.perDayPrice ? formatNumber(parseFloat(adDetail?.perDayPrice)) : '-'}</span>
              </div>
              <div className='ad-payment-row ad-payment-row--total'>
                <span className='ad-payment-row__label flex-shrink-0'>{t('Total Amount')}</span>
                <span className='ad-payment-row__value text-end text-nowrap'>{adDetail?.price ? formatNumber(parseFloat(adDetail?.price)) : '0 UZS'}</span>
              </div>
            </div>

            {adDetail?.paymentMethod && (
              <div className='mt-4'>
                <div className='ad-detail-label mb-2'>{t('Payment Method')}</div>
                <div className='d-flex align-items-center gap-3 p-3 rounded-3' style={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                  {adDetail?.paymentIcon && (
                    <img src={adDetail?.paymentIcon} alt='Payment' className='flex-shrink-0' style={{ width: 36, height: 36, objectFit: 'contain' }} />
                  )}
                  <div className='min-w-0'>
                    <div className='fw-bold text-success text-truncate'>{adDetail?.paymentMethod}</div>
                    {adDetail?.paymentStatus && <span className='fs-8 text-muted'>{adDetail?.paymentStatus}</span>}
                  </div>
                </div>
              </div>
            )}

            {(adDetail?.transactionNumber || adDetail?.transactionId) && (
              <DetailRow label={t('Transaction ID')}>
                <code className='fs-7 text-break d-inline-block' style={{ maxWidth: '100%' }}>
                  {adDetail?.transactionNumber || adDetail?.transactionId}
                </code>
              </DetailRow>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export { ViewBannerAdvertisementDetail }
